import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Input,
  Label,
} from "@win-reality/react-ui";
import { useState } from "react";

import { isValidJerseyNumber } from "../../utils/playerEdit";
import { ThemedDialog } from "../ThemedDialog";

type JerseyNumberDialogProps = {
  playerName: string;
  jerseyNumber?: string;
  onCancel: () => void;
  onSave: (jerseyNumber: string) => void;
  /** Shows Save loading while the number saves */
  isSaving?: boolean;
};

/**
 * Player Details: the player's jersey number for this team. Names and
 * handedness are changed in Manage Rosters.
 */
export const JerseyNumberDialog = ({
  playerName,
  jerseyNumber: initialJerseyNumber = "",
  onCancel,
  onSave,
  isSaving = false,
}: JerseyNumberDialogProps) => {
  const [jerseyNumber, setJerseyNumber] = useState(initialJerseyNumber);
  const value = jerseyNumber.trim();
  const isValid = isValidJerseyNumber(value);
  const canSave = isValid && value !== initialJerseyNumber && !isSaving;

  return (
    <ThemedDialog
      open
      onOpenChange={(open) => !open && !isSaving && onCancel()}
      defaultCancelButton={false}
      size="sm"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">
                Player Details
              </DialogTitle>
            </DialogHeader>
            <form
              id="jersey-number-form"
              className="space-y-4 pb-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (canSave) onSave(value);
              }}
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {playerName}
              </p>
              <div className="space-y-2">
                <Label
                  htmlFor="jersey-number"
                  className="text-sm text-gray-900 dark:text-white"
                >
                  Jersey Number
                </Label>
                <Input
                  id="jersey-number"
                  inputMode="numeric"
                  value={jerseyNumber}
                  onChange={(event) => setJerseyNumber(event.target.value)}
                  isDisabled={isSaving}
                  aria-invalid={value !== "" && !isValid}
                  data-testid="plate-appearance-editor-jersey-number-input"
                  data-value={value}
                />
                {value !== "" && !isValid && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Enter a number from 0 to 999.
                  </p>
                )}
              </div>
              <p className="text-sm text-tagging-muted">
                To change the player's name or handedness, use Manage Rosters.
              </p>
            </form>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full items-center justify-between">
              <Button
                variant="ghost"
                onClick={onCancel}
                isDisabled={isSaving}
                data-testid="plate-appearance-editor-jersey-number-dialog-cancel-button"
                className="underline"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="jersey-number-form"
                variant="secondary-flat-light"
                isDisabled={!canSave}
                isLoading={isSaving}
                aria-busy={isSaving}
                data-testid="plate-appearance-editor-jersey-number-dialog-confirm-button"
                className="min-w-[136px] rounded-full"
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </ThemedDialog>
  );
};
